import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import PublicLayout from './public/PublicLayout';
import StripePaymentForm from './StripePaymentForm';
import PaymentSuccessModal from './PaymentSuccessModal';

// Initialize Stripe (will load publishable key from backend)
let stripePromise = null;

const CoursePaymentPage = ({ user, onLogout }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [paymentMethod, setPaymentMethod] = useState('card');
    const [processing, setProcessing] = useState(false);
    const [showWidget, setShowWidget] = useState(false);
    const [clientSecret, setClientSecret] = useState('');
    const [stripePublicKey, setStripePublicKey] = useState('');
    const [showSuccessModal, setShowSuccessModal] = useState(false);

    // Get course details from navigation state
    const courseDetails = location.state || {};
    const { courseId, courseName, amount, studentInfo } = courseDetails;

    // Load Stripe publishable key on component mount
    useEffect(() => {
        const loadStripeKey = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/stripe/publishable-key`);
                const data = await response.json();
                if (data.success && data.publishableKey) {
                    setStripePublicKey(data.publishableKey);
                    stripePromise = loadStripe(data.publishableKey);
                }
            } catch (error) {
                console.error('Error loading Stripe key:', error);
            }
        };
        loadStripeKey();
    }, []);

    // Create payment intent when user selects card payment
    useEffect(() => {
        if (paymentMethod === 'card' && showWidget && amount && !clientSecret) {
            createPaymentIntent();
        }
    }, [paymentMethod, showWidget, amount]);

    const createPaymentIntent = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/stripe/create-course-payment-intent`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    amount,
                    courseId,
                    courseName,
                    userId: user?.id || 'guest',
                    email: studentInfo?.email || user?.email || ''
                }),
            });

            const data = await response.json();
            if (data.success && data.clientSecret) {
                setClientSecret(data.clientSecret);
            } else {
                alert('Failed to initialize payment. Please try again.');
                setShowWidget(false);
            }
        } catch (error) {
            console.error('Error creating payment intent:', error);
            alert('Failed to initialize payment. Please try again.');
            setShowWidget(false);
        }
    };

    useEffect(() => {
        // If no course details, redirect back
        if (!courseId || !amount) {
            navigate('/virtual-university');
        }
    }, [courseId, amount, navigate]);

    const enrollStudentAfterPayment = async (paymentData) => {
        try {
            const enrollmentData = {
                courseId,
                studentInfo,
                paymentInfo: {
                    paymentId: paymentData.paymentId,
                    amount: paymentData.amount,
                    status: paymentData.status
                },
                amount: paymentData.amount,
                currency: 'USD'
            };

            // Get authentication token
            const token = localStorage.getItem('userToken') ||
                localStorage.getItem('directorToken') ||
                localStorage.getItem('adminToken') ||
                localStorage.getItem('token');

            if (!token) {
                alert('Authentication required. Please log in.');
                navigate('/member-login');
                return;
            }

            const API_BASE = process.env.REACT_APP_API_URL;

            if (!API_BASE) {
                console.error('❌ REACT_APP_API_URL not configured!');
                alert('System configuration error. Please contact support.');
                return;
            }

            const response = await fetch(`${API_BASE}/university/enroll`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(enrollmentData)
            });

            if (response.ok) {
                const responseData = await response.json();
                console.log('✅ Enrollment successful:', responseData);
                setShowSuccessModal(true);
            } else {
                const errorData = await response.json();
                console.error('❌ Enrollment failed:', errorData);
                alert(`Enrollment failed: ${errorData.message || 'Unknown error'}`);
            }
        } catch (error) {
            console.error('Enrollment error:', error);
            alert(`Enrollment failed: ${error.message}`);
        }
    };

    const handlePayment = async () => {
        setProcessing(true);

        try {
            // Handle Mobile Money - redirect to Paystack
            if (paymentMethod === 'mobile') {
                window.open('https://paystack.shop/pay/africaresp', '_blank');

                alert(`You will be redirected to Paystack for Mobile Money payment.
        
After completing payment, please return to this page.

Course: ${courseName}
Amount: $${amount}

You will receive course access details via email after payment.`);

                setTimeout(() => {
                    navigate('/virtual-university');
                }, 3000);

                setProcessing(false);
                return;
            }

            // Handle Card Payment - Show Stripe Payment Form
            if (paymentMethod === 'card') {
                setShowWidget(true);
                setProcessing(false);
                return;
            }

            // Handle Bank Transfer - Show success message
            if (paymentMethod === 'bank') {
                alert(`Thank you for choosing Bank Transfer!

Please use the bank details shown on the page to complete your payment.

Course: ${courseName}
Amount: $${amount}

After payment, please email your payment confirmation to admin@africaresp.org

You will receive course access details once your payment is verified.`);

                setTimeout(() => {
                    navigate('/virtual-university');
                }, 2000);

                return;
            }

        } catch (error) {
            console.error('Payment error:', error);
            alert('Payment failed. Please try again.');
        } finally {
            setProcessing(false);
        }
    };

    if (!courseId || !amount) {
        return null; // Will redirect in useEffect
    }

    return (
        <PublicLayout>
            <div className="bg-light py-5">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-lg-8">
                            {/* Page Header */}
                            <div className="mb-4">
                                <button
                                    onClick={() => navigate('/virtual-university')}
                                    className="btn btn-outline-secondary btn-sm mb-3"
                                >
                                    <i className="fas fa-arrow-left me-2"></i>
                                    Back to ARCA Virtual University
                                </button>
                                <h1 className="h3 fw-bold text-dark">Course Enrollment Payment</h1>
                                <p className="text-muted">Complete payment for your course enrollment</p>
                            </div>

                            <div className="row g-4">
                                {/* Order Summary */}
                                <div className="col-lg-5">
                                    <div className="card border-0 shadow-sm sticky-top" style={{ top: '20px' }}>
                                        <div className="card-header bg-white border-bottom">
                                            <h5 className="mb-0 fw-bold">Enrollment Summary</h5>
                                        </div>
                                        <div className="card-body">
                                            <div className="mb-3">
                                                <h6 className="fw-bold text-dark">{courseName}</h6>
                                                <p className="text-muted small mb-0">
                                                    <i className="fas fa-graduation-cap me-2"></i>
                                                    ARCA Virtual University
                                                </p>
                                            </div>

                                            <hr />

                                            <div className="d-flex justify-content-between mb-2">
                                                <span className="text-muted">Course Fee:</span>
                                                <span className="fw-medium">${amount}.00</span>
                                            </div>

                                            <hr />

                                            <div className="d-flex justify-content-between mb-3">
                                                <span className="fw-bold h5 mb-0">Total:</span>
                                                <span className="fw-bold h5 mb-0" style={{ color: '#6B8E23' }}>${amount}.00</span>
                                            </div>

                                            <div className="alert alert-info small mb-0">
                                                <i className="fas fa-info-circle me-2"></i>
                                                You will receive course access details via email after payment is processed.
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Payment Form */}
                                <div className="col-lg-7">
                                    <div className="card border-0 shadow-sm">
                                        <div className="card-header bg-white border-bottom">
                                            <h5 className="mb-0 fw-bold">Payment Method</h5>
                                        </div>
                                        <div className="card-body p-4">
                                            {!showWidget ? (
                                                <>
                                                    {/* Payment Method Selection */}
                                                    <div className="mb-4">
                                                        <label className="form-label fw-bold">Select Payment Method</label>

                                                        {/* Card Payment */}
                                                        <div
                                                            className={`card mb-3 cursor-pointer ${paymentMethod === 'card' ? 'border-success border-2' : ''}`}
                                                            style={{ cursor: 'pointer' }}
                                                            onClick={() => setPaymentMethod('card')}
                                                        >
                                                            <div className="card-body d-flex align-items-center">
                                                                <input
                                                                    type="radio"
                                                                    name="payment"
                                                                    checked={paymentMethod === 'card'}
                                                                    onChange={() => setPaymentMethod('card')}
                                                                    className="form-check-input me-3"
                                                                />
                                                                <div className="flex-grow-1">
                                                                    <h6 className="mb-0 fw-bold">Credit/Debit Card</h6>
                                                                    <small className="text-muted">Visa, Mastercard, American Express via Stripe</small>
                                                                </div>
                                                                <div className="d-flex gap-2">
                                                                    <i className="fab fa-cc-visa fa-2x text-primary"></i>
                                                                    <i className="fab fa-cc-mastercard fa-2x" style={{ color: '#eb001b' }}></i>
                                                                    <i className="fab fa-cc-amex fa-2x" style={{ color: '#006fcf' }}></i>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Mobile Money */}
                                                        <div
                                                            className={`card mb-3 cursor-pointer ${paymentMethod === 'mobile' ? 'border-success border-2' : ''}`}
                                                            style={{ cursor: 'pointer' }}
                                                            onClick={() => setPaymentMethod('mobile')}
                                                        >
                                                            <div className="card-body d-flex align-items-center">
                                                                <input
                                                                    type="radio"
                                                                    name="payment"
                                                                    checked={paymentMethod === 'mobile'}
                                                                    onChange={() => setPaymentMethod('mobile')}
                                                                    className="form-check-input me-3"
                                                                />
                                                                <div className="flex-grow-1">
                                                                    <h6 className="mb-0 fw-bold">Mobile Money</h6>
                                                                    <small className="text-muted">M-Pesa, MTN, Airtel Money via Paystack</small>
                                                                </div>
                                                                <i className="fas fa-mobile-alt fa-2x" style={{ color: '#6B8E23' }}></i>
                                                            </div>
                                                        </div>

                                                        {/* Bank Transfer */}
                                                        <div
                                                            className={`card mb-3 cursor-pointer ${paymentMethod === 'bank' ? 'border-success border-2' : ''}`}
                                                            style={{ cursor: 'pointer' }}
                                                            onClick={() => setPaymentMethod('bank')}
                                                        >
                                                            <div className="card-body d-flex align-items-center">
                                                                <input
                                                                    type="radio"
                                                                    name="payment"
                                                                    checked={paymentMethod === 'bank'}
                                                                    onChange={() => setPaymentMethod('bank')}
                                                                    className="form-check-input me-3"
                                                                />
                                                                <div className="flex-grow-1">
                                                                    <h6 className="mb-0 fw-bold">Bank Transfer</h6>
                                                                    <small className="text-muted">Direct bank transfer (International & Local)</small>
                                                                </div>
                                                                <i className="fas fa-university fa-2x" style={{ color: '#6B8E23' }}></i>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Payment Instructions (Bank Transfer details would go here if needed) */}

                                                    {/* Payment Button */}
                                                    <button
                                                        className="btn btn-lg w-100 text-white fw-bold mt-3"
                                                        style={{ backgroundColor: '#6B8E23', border: 'none' }}
                                                        onClick={handlePayment}
                                                        disabled={processing}
                                                    >
                                                        {processing ? (
                                                            <>
                                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                                Processing...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <i className="fas fa-lock me-2"></i>
                                                                Complete Payment ${amount}
                                                            </>
                                                        )}
                                                    </button>

                                                    <div className="text-center mt-3">
                                                        <small className="text-muted">
                                                            <i className="fas fa-shield-alt me-1"></i>
                                                            Secure payment powered by ARCA
                                                        </small>
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    {/* Stripe Payment Form */}
                                                    <div className="mb-3">
                                                        <button
                                                            onClick={() => {
                                                                setShowWidget(false);
                                                                setClientSecret('');
                                                            }}
                                                            className="btn btn-sm btn-outline-secondary mb-3"
                                                        >
                                                            <i className="fas fa-arrow-left me-2"></i>
                                                            Back to Payment Methods
                                                        </button>
                                                    </div>

                                                    <div className="alert alert-info mb-3">
                                                        <i className="fas fa-info-circle me-2"></i>
                                                        Complete your payment securely below. You will receive course access details via email.
                                                    </div>

                                                    {/* Stripe Elements */}
                                                    {clientSecret && stripePublicKey && stripePromise ? (
                                                        <Elements stripe={stripePromise} options={{ clientSecret }}>
                                                            <StripePaymentForm
                                                                amount={amount}
                                                                courseId={courseId}
                                                                studentInfo={studentInfo}
                                                                onSuccess={(paymentData) => {
                                                                    console.log('✅ Payment completed, enrolling student...', paymentData);
                                                                    // Now enroll student with payment proof
                                                                    enrollStudentAfterPayment(paymentData);
                                                                }}
                                                                onError={(error) => {
                                                                    console.error('Payment error:', error);
                                                                }}
                                                            />
                                                        </Elements>
                                                    ) : (
                                                        <div className="text-center py-5">
                                                            <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                                                                <span className="visually-hidden">Loading payment form...</span>
                                                            </div>
                                                            <p className="mt-3 text-muted">Initializing secure payment...</p>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Payment Success Modal */}
            <PaymentSuccessModal
                show={showSuccessModal}
                onClose={() => setShowSuccessModal(false)}
                paymentType="course"
                details={{
                    name: courseName,
                    amount: amount
                }}
            />
        </PublicLayout>
    );
};

export default CoursePaymentPage;
